import { useRateFieldError } from "../../../hooks/useRateFieldError";
import { FC } from "react";

import { Box } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";

import { useRateContext } from "../../../Context/RateContext";
import { useRateFieldName } from "../../../hooks/useRateFieldName";
import { useEnableMemberOnly } from "../../../hooks/useEnableMemberOnly";
import { MemberOnlyMembershipsField } from "../../../RateFields/MemberOnlyMembershipsField/MemberOnlyMembershipsField";
import { RateOptionToggleRow } from "../RateOptionToggleRow";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Card } from "@/icons/outlined";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

export const MemberOnlyRow: FC = () => {
  const { t } = useTranslate();
  const { logAmplitudeEvent } = useAmplitude();
  const { formCategory, isEditing, isOnboarding } = useRateContext();
  const { control, setValue } = useFormContext<StepTicketsFormData>();

  const fieldName = useRateFieldName("isMemberOnly");
  const isChecked = useWatch({ control, name: fieldName });
  const error = useRateFieldError("memberOnlyMembershipRateIds");

  const { hasMemberships, canEnableMemberOnly, applyMembershipSelection } = useEnableMemberOnly();

  const handleToggle = (checked: boolean) => {
    setValue(fieldName, checked, { shouldDirty: true });
    applyMembershipSelection(checked);
    logAmplitudeEvent(AmplitudeEvents.TicketOptionToggled, {
      option: "members_only",
      enabled: checked,
      entry_point: "manual",
      form_type: formCategory,
      is_liveform: true,
      isEditing,
      isOnboarding,
    });
  };

  if (!hasMemberships) {
    return null;
  }

  return (
    <RateOptionToggleRow
      optionKey="memberOnly"
      checked={Boolean(isChecked)}
      onToggle={handleToggle}
      disabled={!isChecked && !canEnableMemberOnly}
      icon={<Card />}
      label={t("dashboard", "ticketing.rateOption.memberOnly")}
      tooltip={t("dashboard", "ticketing.memberOnly.isMemberOnlyTooltip")}
      inlineConfig={isChecked ? <MemberOnlyMembershipsField variant="inline" /> : undefined}
      belowContent={
        Boolean(isChecked) && error?.message ? (
          <Box component="span">
            <DashboardTypography component="p" variant="caption" vibe="error-main">
              {error.message}
            </DashboardTypography>
          </Box>
        ) : undefined
      }
    />
  );
};
